import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { PermissionSlug } from '../constants/featureFlags';
import { useShallow } from 'zustand/react/shallow';
import {
  getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  listBespokeTemplatesBespokeTemplatesGet,
  listBespokeTemplateRevisionsBespokeTemplatesRevisionsGet,
  createBespokeTemplateBespokeTemplatesPost,
  updateBespokeTemplateBespokeTemplatesTemplateIdPatch,
  deleteBespokeTemplateBespokeTemplatesTemplateIdDelete,
} from '../services/api/sdk.gen';
import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  SectionOrderItem,
  VersionedQuestionSchema,
  BespokeTemplateResponse,
  BespokeTemplateRevision,
  BespokeTemplateCreate,
  BespokeTemplateUpdate,
} from '../services/api/types.gen';
import type { VisitTypeId } from '../services/api/models/VisitTypeId';
import type { RogerDocFormType } from '../services/api/models/RogerDocFormType';
import { useToast } from '../hooks/useToastContext';
import { fetchRogerEmrFormSchemaVersions } from './formSchema/fetchRogerEmrFormSchemaVersions';
import { Card, CardContent, CardHeader, CardTitle } from '@roger/ui-components';
import { SectionVersionsDialog } from './formSchema/SectionVersionsDialog';
import { ValidationResultsDialog } from './formSchema/ValidationResultsDialog';
import { pickInitialVersion } from './formSchema/pickInitialVersion';
import {
  isSectionEditorDraftDirty,
  shouldAdoptActiveTemplateDraft,
  type SectionEditorDraftSeed,
} from '../store/slices/formSchemaEditorSlice';
import { formatSectionDropdownLabel } from './formSchema/formatSectionDropdownLabel';
import { ShieldX } from 'lucide-react';
import type {
  FormTree,
  FormTreeQuestion,
  SectionLayoutState,
} from './formSchema/formSchemaTypes';
import {
  acknowledgeRemotePush,
  applyRemotePushDelta,
  bespokeApiErrorMessage,
  bespokeTemplateRevisionsFingerprint,
  clearRemotePush,
  defaultBlankSectionLayoutName,
  demotedPrimarySiblingIdsForPromotion,
  diffRevisionsForRemotePush,
  enabledTemplatesBySectionForPin,
  filterBespokeTemplatesForPin,
  isPersistedInactiveLayoutDraft,
  layoutGenerationKey,
  orderedFormTreeSectionIds,
  pinFromFormSchemaVersion,
  pinsFromFormSchemaVersions,
  planRevisionsPollFingerprintRefresh,
  removeBespokeTemplateLocal,
  requireSavedBespokeTemplate,
  sectionEditorDraftKey,
  unacknowledgedRemotePush,
  upsertBespokeTemplatesLocal,
  validateLayoutJsonForSection,
  type RemotePushByTemplateId,
  type RemotePushRevisionSnapshot,
} from './formSchema/formSchemaUtils';
import { collectQuestionTreeQids } from '../components/oasis/walkQuestionTree';
import {
  buildLiveConsistencyIndexes,
  SCHEMA_UNKNOWN,
  sectionConsistencyForTemplate,
  type SectionConsistency,
} from './formSchema/sectionConsistency';
import { QuestionsDialog } from './formSchema/QuestionsDialog';
import { BespokeTemplatesImportDialog } from './formSchema/BespokeTemplatesImportDialog';
import { BespokeTemplatesExportDialog } from './formSchema/BespokeTemplatesExportDialog';
import { BulkTemplateStatusDialog } from './formSchema/BulkTemplateStatusDialog';
import { useBulkTemplateStatus } from './formSchema/useBulkTemplateStatus';
import {
  CustomGenerateDialog,
  DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT,
  type CustomGenerateTarget,
} from './formSchema/CustomGenerateDialog';
import { FormSchemasHeader } from './formSchema/FormSchemasHeader';
import { SchemaVersionBrowser } from './formSchema/SchemaVersionBrowser';
import { FormSchemaStudio } from './formSchema/FormSchemaStudio';
import type { PreviewViewport } from './formSchema/previewViewport';
import {
  FORM_SCHEMA_QUERY_PARAMS,
  layoutTemplateQueryValue,
} from './formSchema/formSchemaQueryParams';
import {
  editorTargetFromPersistedDraft,
  type EditorTarget,
} from './formSchema/editorTarget';
import { bespokeStatusTier } from './formSchema/bulkTemplateStatus';

const FormSchemasWorkspace: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlStateRef = useRef({
    formType: searchParams.get(
      FORM_SCHEMA_QUERY_PARAMS.formType
    ) as RogerDocFormType | null,
    schemaName: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.schemaName),
    schemaVersion: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.schemaVersion),
    section: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.section),
    layoutTemplate: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.layoutTemplate),
  });
  /** After first auto-select, only URL params matching the active form type apply. */
  const initialVersionUrlConsumedRef = useRef(false);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const sectionGenerationStatusByKey = useStore(
    useShallow(state => state.sectionGenerationStatusByKey)
  );
  const startFormSchemaLayoutGeneration = useStore(
    state => state.startFormSchemaLayoutGeneration
  );

  // Per-section editor working drafts (survive section switches).
  const editorDraftByKey = useStore(
    useShallow(state => state.editorDraftByKey)
  );
  const hydrateSectionEditorDraft = useStore(
    state => state.hydrateSectionEditorDraft
  );
  const setSectionEditorDraft = useStore(state => state.setSectionEditorDraft);
  const setSectionEditorField = useStore(state => state.setSectionEditorField);
  const syncSectionEditorTemplateDetails = useStore(
    state => state.syncSectionEditorTemplateDetails
  );
  const markSectionEditorSaved = useStore(
    state => state.markSectionEditorSaved
  );

  const updateUrlSelection = useCallback(
    (next: {
      formType?: RogerDocFormType | null;
      schemaName?: string | null;
      schemaVersion?: string | null;
      section?: string | null;
      layoutTemplate?: string | null;
    }) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          const setOrDelete = (
            key: string,
            value: string | null | undefined
          ) => {
            if (value == null || value === '') params.delete(key);
            else params.set(key, value);
          };
          if ('formType' in next) {
            setOrDelete(FORM_SCHEMA_QUERY_PARAMS.formType, next.formType);
          }
          if ('schemaName' in next) {
            setOrDelete(FORM_SCHEMA_QUERY_PARAMS.schemaName, next.schemaName);
          }
          if ('schemaVersion' in next) {
            setOrDelete(
              FORM_SCHEMA_QUERY_PARAMS.schemaVersion,
              next.schemaVersion
            );
          }
          if ('section' in next) {
            setOrDelete(FORM_SCHEMA_QUERY_PARAMS.section, next.section);
          }
          if ('layoutTemplate' in next) {
            setOrDelete(
              FORM_SCHEMA_QUERY_PARAMS.layoutTemplate,
              next.layoutTemplate
            );
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [formTypeCatalog, setFormTypeCatalog] = useState<
    RogerDocFormTypeCatalogEntry[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // -- state: schema versions browser --
  const [selectedRogerDocFormType, setSelectedRogerDocFormType] =
    useState<RogerDocFormType | null>(null);
  const [versions, setVersions] = useState<FormSchemaVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  // -- state: form tree preview --
  const [selectedVersion, setSelectedVersion] =
    useState<FormSchemaVersion | null>(null);
  // Lives in the workspace (not FormSchemaStudio): changing form type clears
  // selectedVersion and remounts the studio; keep the phone/tablet/print
  // preview choice across that transition.
  const [previewViewport, setPreviewViewport] =
    useState<PreviewViewport>('laptop');
  const [formTree, setFormTree] = useState<FormTree | null>(null);
  // Mirrors the FSV's `section_order` for the active form-tree preview, so
  // the studio sidebar/table use the same order + display labels the visit
  // UI does. `null` when the FSV doesn't ship one (older RogerDoc deploys).
  const [formTreeSectionOrder, setFormTreeSectionOrder] = useState<
    SectionOrderItem[] | null
  >(null);
  const [treeLoading, setTreeLoading] = useState(false);

  // -- state: bespoke templates --
  const [templates, setTemplates] = useState<BespokeTemplateResponse[]>([]);
  const [templatesReady, setTemplatesReady] = useState(false);
  // True when the last catalog fetch failed. The template list isn't
  // version-scoped, so schema-version switches no longer refetch it — but if
  // the initial load failed we still want the next version switch to retry
  // rather than leave the page permanently empty.
  const [templatesLoadFailed, setTemplatesLoadFailed] = useState(false);
  const [templateStatusBusyId, setTemplateStatusBusyId] = useState<
    string | null
  >(null);

  const selectedCatalogEntry = useMemo(
    () =>
      formTypeCatalog.find(r => r.form_type === selectedRogerDocFormType) ??
      null,
    [formTypeCatalog, selectedRogerDocFormType]
  );

  const canonicalVisitForApi: VisitTypeId | null =
    selectedCatalogEntry?.canonical_visit_type_id ?? null;

  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [versionsSection, setVersionsSection] = useState<string | null>(null);
  const [questionDialog, setQuestionDialog] = useState<{
    sectionName: string;
    questions: FormTreeQuestion[];
  } | null>(null);
  const [customGenerateDialog, setCustomGenerateDialog] =
    useState<CustomGenerateTarget | null>(null);
  const [selectedSectionName, setSelectedSectionName] = useState<string | null>(
    null
  );
  const [saving, setSaving] = useState(false);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [validationResultsOpen, setValidationResultsOpen] = useState(false);

  const [importModalOpen, setImportModalOpen] = useState(false);

  const getCanonicalVisitForFormType = useCallback(
    (formType: RogerDocFormType | null | undefined): VisitTypeId | null =>
      formTypeCatalog.find(row => row.form_type === formType)
        ?.canonical_visit_type_id ?? null,
    [formTypeCatalog]
  );

  // -- fetch schema versions when visit type changes --
  const fetchVersions = useCallback(async (visitTypeApi: VisitTypeId) => {
    setVersionsLoading(true);
    setVersionsError(null);
    setSelectedVersion(null);
    setFormTree(null);
    setFormTreeSectionOrder(null);
    try {
      setVersions(await fetchRogerEmrFormSchemaVersions(visitTypeApi));
    } catch (err) {
      console.error('Failed to fetch schema versions:', err);
      setVersions([]);
      setVersionsError(
        bespokeApiErrorMessage(err) ||
          'Could not load schema versions for this form type.'
      );
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setCatalogLoading(true);
      try {
        const res = await getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet(
          {}
        );
        const rows =
          (res?.data as RogerDocFormTypeCatalogEntry[] | undefined) ?? [];
        if (cancelled) return;
        setFormTypeCatalog(rows);
        setSelectedRogerDocFormType(prev => {
          if (prev != null && rows.some(r => r.form_type === prev)) {
            return prev;
          }
          const urlFormType = initialUrlStateRef.current.formType;
          if (
            urlFormType != null &&
            rows.some(r => r.form_type === urlFormType)
          ) {
            return urlFormType;
          }
          return rows[0]?.form_type ?? null;
        });
      } catch (err) {
        console.error('Failed to load Roger Doc form type catalog:', err);
        if (!cancelled) {
          setFormTypeCatalog([]);
          setSelectedRogerDocFormType(null);
          toastRef.current.error(
            'Catalog unavailable',
            'Could not load Roger Doc form types. Refresh to retry.'
          );
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (canonicalVisitForApi == null) return;
    void fetchVersions(canonicalVisitForApi);
  }, [canonicalVisitForApi, fetchVersions]);

  /**
   * `section_id → display_name` for the active form tree (ROG-2295). Empty
   * when the FSV doesn't ship `section_order`; `formatSectionDropdownLabel`
   * falls back to the legacy underscore-strip transform in that case.
   */
  const formTreeLabelByCategory = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const item of formTreeSectionOrder ?? []) {
      lookup[item.section_id] = item.display_name;
    }
    return lookup;
  }, [formTreeSectionOrder]);

  /**
   * Section keys for the active form tree in the FSV's preferred order.
   * Falls back to `Object.keys(formTree)` when no section_order is supplied
   * so studio behavior is unchanged for legacy FSVs.
   */
  const orderedFormTreeSections = useMemo<string[]>(() => {
    if (!formTree) return [];
    return orderedFormTreeSectionIds(formTree, formTreeSectionOrder);
  }, [formTree, formTreeSectionOrder]);

  useEffect(() => {
    if (!formTree) {
      setSelectedSectionName(null);
      return;
    }

    const sections = orderedFormTreeSections;
    setSelectedSectionName(prev =>
      initialUrlStateRef.current.section &&
      sections.includes(initialUrlStateRef.current.section)
        ? initialUrlStateRef.current.section
        : prev && sections.includes(prev)
          ? prev
          : (sections[0] ?? null)
    );
  }, [formTree, orderedFormTreeSections]);

  // -- fetch the form tree for a selected version --
  // The build endpoint returns {version, schema_data} for a specific
  // (name, version) selector; we use schema_data as the form tree, plus
  // version.section_order to drive section order + display labels.
  const fetchFormTreeData = useCallback(
    async (
      version: FormSchemaVersion
    ): Promise<{
      tree: FormTree | null;
      sectionOrder: SectionOrderItem[] | null;
    }> => {
      const visitTypeApi =
        getCanonicalVisitForFormType(version.form_type as RogerDocFormType) ??
        canonicalVisitForApi;
      if (visitTypeApi == null) return { tree: null, sectionOrder: null };
      const res = await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
        query: { visit_type: visitTypeApi },
        body: {
          schema_version: {
            name: version.name,
            version: version.version ?? null,
          },
          // Forms Manager authors against the same question set the MCP sees.
          omit_inline_goals: true,
        },
      });
      const payload = res?.data as VersionedQuestionSchema | undefined;
      if (!payload) return { tree: null, sectionOrder: null };
      return {
        tree: payload.schema_data as FormTree,
        sectionOrder: payload.version.section_order ?? null,
      };
    },
    [canonicalVisitForApi, getCanonicalVisitForFormType]
  );

  const fetchFormTree = useCallback(
    async (version: FormSchemaVersion) => {
      setTreeLoading(true);
      try {
        const { tree, sectionOrder } = await fetchFormTreeData(version);
        setFormTree(tree);
        setFormTreeSectionOrder(sectionOrder);
      } catch (err) {
        console.error('Failed to fetch form tree:', err);
        setFormTree(null);
        setFormTreeSectionOrder(null);
      } finally {
        setTreeLoading(false);
      }
    },
    [fetchFormTreeData]
  );

  // -- fetch bespoke templates --
  // Returns the fetched list (in addition to setting state) so callers that
  // need the fresh rows synchronously — e.g. the live-refresh poll below —
  // don't have to wait a render cycle for `templates` to catch up.
  const fetchTemplates = useCallback(async (): Promise<
    BespokeTemplateResponse[]
  > => {
    try {
      const res = await listBespokeTemplatesBespokeTemplatesGet({
        throwOnError: true,
      });
      const list = (res?.data as BespokeTemplateResponse[] | undefined) ?? [];
      setTemplates(list);
      setTemplatesLoadFailed(false);
      return list;
    } catch (err) {
      console.error('Failed to fetch bespoke templates:', err);
      setTemplates([]);
      setTemplatesLoadFailed(true);
      return [];
    } finally {
      setTemplatesReady(true);
    }
  }, []);

  /**
   * Retry the catalog load only when the previous fetch actually failed. Keeps
   * healthy schema-version switches refetch-free (the list isn't version-scoped)
   * while giving a page that failed its first load a second chance instead of
   * staying empty forever.
   */
  const retryTemplatesIfLoadFailed = useCallback(() => {
    if (templatesLoadFailed) void fetchTemplates();
  }, [fetchTemplates, templatesLoadFailed]);

  /**
   * Apply the response of a single-row create/update directly to local
   * state instead of refetching the entire (unscoped) template list.
   *
   * The list from `fetchTemplates()` isn't version/pin-scoped — it's every
   * bespoke template everywhere — so refetching it after a one-row mutation
   * replaces every *other* row with a brand-new object too. That churns
   * memoized derivations (`enabledTemplateBySection`, `editorTarget`, ...)
   * and re-triggers effects downstream that key off object identity rather
   * than content (e.g. the open section editor's RogerDoc schema fetch),
   * causing an avoidable RogerDoc round-trip after every single-section
   * copy or edit.
   *
   * Exception: when the prior catalog load failed, local state is empty and
   * untrusted — patching in just the saved row would make the page look like
   * a one-template catalog. Heal with a full `fetchTemplates()` instead.
   *
   * `upsertBespokeTemplatesLocal` also mirrors the backend's single-primary
   * rule so promoting/creating a live row demotes the section's stale active
   * row locally (see the helper for details).
   */
  const upsertTemplatesLocal = useCallback(
    (saved: readonly BespokeTemplateResponse[]) => {
      if (saved.length === 0) return;
      if (templatesLoadFailed) {
        void fetchTemplates();
        return;
      }
      setTemplates(prev => upsertBespokeTemplatesLocal(prev, saved));
    },
    [fetchTemplates, templatesLoadFailed]
  );

  const upsertTemplateLocal = useCallback(
    (saved: BespokeTemplateResponse) => upsertTemplatesLocal([saved]),
    [upsertTemplatesLocal]
  );

  const removeTemplateLocal = useCallback((id: string) => {
    setTemplates(prev => removeBespokeTemplateLocal(prev, id));
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  const {
    bulkBespokeDialog,
    bulkBespokeBusy,
    openBulkBespokeDialog,
    closeBulkBespokeDialog,
    runBulkBespokeConfirmed,
  } = useBulkTemplateStatus({
    templates,
    onUpdated: fetchTemplates,
  });

  useEffect(() => {
    if (selectedVersion || versionsLoading || versions.length === 0) return;
    if (
      selectedRogerDocFormType != null &&
      versions.some(v => v.form_type !== selectedRogerDocFormType)
    ) {
      return;
    }

    const urlMatchesFormType =
      !initialVersionUrlConsumedRef.current &&
      initialUrlStateRef.current.formType === selectedRogerDocFormType;
    const { schemaName, schemaVersion } = urlMatchesFormType
      ? initialUrlStateRef.current
      : { schemaName: null, schemaVersion: null };
    const match = pickInitialVersion(versions, { schemaName, schemaVersion });
    if (!match) return;
    initialVersionUrlConsumedRef.current = true;
    setSelectedVersion(match);
    void fetchFormTree(match);
    // NOTE: no fetchTemplates() here — the bespoke template list isn't
    // version/pin-scoped (it's every template everywhere), so switching the
    // selected schema version never changes what that list should contain.
  }, [
    fetchFormTree,
    selectedRogerDocFormType,
    selectedVersion,
    versions,
    versionsLoading,
  ]);

  const handleVersionClick = (v: FormSchemaVersion) => {
    setSelectedVersion(v);
    setSelectedSectionName(null);
    updateUrlSelection({
      formType: v.form_type ? (v.form_type as RogerDocFormType) : null,
      schemaName: v.name,
      schemaVersion: `${v.version ?? ''}`,
      section: null,
      layoutTemplate: null,
    });
    void fetchFormTree(v);
    retryTemplatesIfLoadFailed();
  };

  const handleSectionClick = (sectionName: string) => {
    setSelectedSectionName(sectionName);
    updateUrlSelection({ section: sectionName, layoutTemplate: null });
  };

  // -- bespoke CRUD handlers --
  const handlePersistVibecoderJson = async (
    payload: BespokeTemplateCreate | BespokeTemplateUpdate
  ) => {
    if (!selectedVersion || !selectedSectionName || !selectedRogerDocFormType)
      return;
    const key = sectionEditorDraftKey(
      selectedRogerDocFormType,
      selectedVersion.name,
      selectedSectionName
    );
    const draft = editorDraftByKey[key];
    const templateId = draft?.editingTemplateId ?? null;
    const submittedSnapshot = draft
      ? {
          layoutJsonText: draft.layoutJsonText,
          name: draft.name,
          description: draft.description,
          status: draft.status,
        }
      : undefined;
    setSaving(true);
    try {
      let saved: BespokeTemplateResponse;
      if (templateId) {
        const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
          path: { template_id: templateId },
          body: payload as BespokeTemplateUpdate,
          throwOnError: true,
        });
        // Fail closed before celebrating success: without the saved row we
        // can't patch local state and the list would keep the stale active row.
        saved = requireSavedBespokeTemplate(res?.data);
        toast.success('Template Updated', 'Roger Layout Schema saved.');
      } else {
        const res = await createBespokeTemplateBespokeTemplatesPost({
          body: payload as BespokeTemplateCreate,
          throwOnError: true,
        });
        saved = requireSavedBespokeTemplate(res?.data);
        toast.success('Template Created', 'JSON bespoke template registered.');
      }
      // A local save's own `updated_at` bump is not a remote push: suppress it
      // for the next poll cycle and drop any stale push entry immediately so
      // the dot/pill don't show against content we just saved ourselves.
      ownSaveTemplateIdsRef.current.add(saved.id);
      setRemotePushByTemplateId(prev => clearRemotePush(prev, saved.id));
      setEditorTarget({ editingTemplate: saved, draft: null });
      if (selectedVersion) {
        markSectionEditorSaved(
          sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            saved.section_name
          ),
          saved,
          submittedSnapshot
        );
      }
      const active = enabledTemplateBySection.get(saved.section_name) ?? null;
      const nextActive = saved.status === 'live' ? saved : active;
      updateUrlSelection({
        layoutTemplate: layoutTemplateQueryValue(saved, nextActive),
      });
      upsertTemplateLocal(saved);
    } catch (err) {
      console.error('Failed to persist JSON bespoke template:', err);
      toast.error('Save Failed', bespokeApiErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBespokeTemplateBespokeTemplatesTemplateIdDelete({
        path: { template_id: id },
        throwOnError: true,
      });
      toast.success('Template Deleted', 'Bespoke template removed.');
      removeTemplateLocal(id);
    } catch (err) {
      console.error('Failed to delete bespoke template:', err);
      toast.error('Delete Failed', bespokeApiErrorMessage(err));
    }
  };

  const handleSetBespokeTemplateStatus = async (
    t: BespokeTemplateResponse,
    nextStatus: BespokeTemplateResponse['status']
  ) => {
    if (t.status === nextStatus) return;
    const demotedSiblingIds =
      nextStatus === 'live' || nextStatus === 'paused'
        ? demotedPrimarySiblingIdsForPromotion(templates, t)
        : [];
    setTemplateStatusBusyId(t.id);
    try {
      const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
        path: { template_id: t.id },
        body: { status: nextStatus },
        throwOnError: true,
      });
      // Fail closed: the server may have promoted this row and demoted a
      // sibling, so a missing body must surface as a failure rather than leave
      // the list showing the old active layout.
      const saved = requireSavedBespokeTemplate(res?.data);
      ownSaveTemplateIdsRef.current.add(saved.id);
      for (const id of demotedSiblingIds) {
        ownSaveTemplateIdsRef.current.add(id);
      }
      setRemotePushByTemplateId(prev => {
        let next = clearRemotePush(prev, saved.id);
        for (const id of demotedSiblingIds) {
          next = clearRemotePush(next, id);
        }
        return next;
      });
      upsertTemplateLocal(saved);
    } catch (err) {
      console.error('Failed to update bespoke template status:', err);
      toast.error('Update Failed', bespokeApiErrorMessage(err));
    } finally {
      setTemplateStatusBusyId(null);
    }
  };

  const handleSaveTemplateDetails = async (
    template: BespokeTemplateResponse,
    details: { name: string; description: string }
  ) => {
    setTemplateStatusBusyId(template.id);
    try {
      const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
        path: { template_id: template.id },
        body: {
          name: details.name,
          description: details.description,
        },
        throwOnError: true,
      });
      // Fail closed before toasting success so the list can't silently drift.
      const saved = requireSavedBespokeTemplate(res?.data);
      ownSaveTemplateIdsRef.current.add(saved.id);
      setRemotePushByTemplateId(prev => clearRemotePush(prev, saved.id));
      const sectionName = template.section_name?.trim();
      if (sectionName && selectedRogerDocFormType) {
        const key = sectionEditorDraftKey(
          selectedRogerDocFormType,
          template.schema_version_name,
          sectionName
        );
        const draft = useStore.getState().editorDraftByKey[key];
        if (draft?.editingTemplateId === template.id) {
          syncSectionEditorTemplateDetails(key, details);
        }
      }
      toast.success('Template updated', 'Name and description saved.');
      upsertTemplateLocal(saved);
    } catch (err) {
      console.error('Failed to update template details:', err);
      toast.error('Update failed', bespokeApiErrorMessage(err));
      throw err;
    } finally {
      setTemplateStatusBusyId(null);
    }
  };

  const pinsForCurrentFormType = useMemo(() => {
    if (!selectedRogerDocFormType) return [];
    return pinsFromFormSchemaVersions(selectedRogerDocFormType, versions);
  }, [selectedRogerDocFormType, versions]);

  /** Full RogerDoc pin for the selected form type + schema version. */
  const selectedPin = useMemo(() => {
    if (!selectedVersion || !selectedRogerDocFormType) return null;
    return pinFromFormSchemaVersion(
      selectedRogerDocFormType as RogerDocFormType,
      selectedVersion
    );
  }, [selectedRogerDocFormType, selectedVersion]);

  /** Rows for the selected pin (form_type disambiguates shared schema names). */
  const visitTypeTemplates = useMemo(() => {
    if (!selectedPin) return [];
    return filterBespokeTemplatesForPin(templates, selectedPin);
  }, [selectedPin, templates]);

  /**
   * Cross-form design consistency, built from every live template (EMR-1410).
   *
   * Live only: comparing against paused or standby rows would report drift no
   * clinician can see. ~94ms over the full catalog, so it is memoised on the
   * template array rather than recomputed per render.
   */
  const consistencyIndexes = useMemo(
    () => buildLiveConsistencyIndexes(templates),
    [templates]
  );

  /** Primary Live or Paused template per section (Live wins) for layout-tree status. */
  const enabledTemplateBySection = useMemo(() => {
    if (!selectedPin) return new Map<string, BespokeTemplateResponse>();
    return enabledTemplatesBySectionForPin(visitTypeTemplates, selectedPin);
  }, [selectedPin, visitTypeTemplates]);

  /**
   * Design-consistency marker state for a section (EMR-1410).
   *
   * Independent of the layout-status marker: a section can have a perfectly
   * valid layout that still styles its questions differently from every other
   * form that shares them.
   */
  const getSectionConsistency = useCallback(
    (section: string): SectionConsistency =>
      sectionConsistencyForTemplate(
        enabledTemplateBySection.get(section),
        consistencyIndexes,
        // The section's own QIDs, so a gate this form cannot express -- a
        // question re-issued under a new id -- is not marked as drift.
        // Before the tree loads the schema is genuinely unknown, which is not
        // the same as a section with no questions: an empty set would suppress
        // every gate and grouping finding and report the section as clean.
        formTree
          ? new Set(collectQuestionTreeQids(formTree[section] ?? []))
          : SCHEMA_UNKNOWN
      ),
    [consistencyIndexes, enabledTemplateBySection, formTree]
  );

  const existingTemplateBySection = useMemo(() => {
    const map = new Map<string, BespokeTemplateResponse>();
    for (const template of visitTypeTemplates) {
      if (template.section_name && !map.has(template.section_name)) {
        map.set(template.section_name, template);
      }
    }
    return map;
  }, [visitTypeTemplates]);

  /** Active template id for the selected section (null = none / blank default). */
  const selectedSectionDefaultTemplateId = selectedSectionName
    ? (enabledTemplateBySection.get(selectedSectionName)?.id ?? null)
    : null;

  /** Build the store-draft seed for a section from a template (or blank). */
  const buildSectionEditorSeed = useCallback(
    (
      sectionName: string,
      template: BespokeTemplateResponse | null
    ): SectionEditorDraftSeed | null => {
      if (!selectedVersion || !selectedRogerDocFormType) return null;
      const identity = {
        schemaVersionName: selectedVersion.name,
        schemaVersionValue: selectedVersion.version ?? null,
        rogerDocFormType: selectedRogerDocFormType,
        sectionName,
      };
      if (template) {
        return {
          ...identity,
          editingTemplateId: template.id,
          layoutJsonText: template.layout_json
            ? JSON.stringify(template.layout_json, null, 2)
            : '[]',
          name: template.name ?? '',
          description: template.description ?? '',
          status: template.status,
        };
      }
      return {
        ...identity,
        editingTemplateId: null,
        layoutJsonText: '[]',
        name: defaultBlankSectionLayoutName(
          formatSectionDropdownLabel(sectionName, formTreeLabelByCategory)
        ),
        description: '',
        status: 'live',
      };
    },
    [selectedVersion, selectedRogerDocFormType, formTreeLabelByCategory]
  );

  // Open the selected section's editor. Per-section drafts survive section
  // switches for the active template; returning to a section without
  // `layoutTemplate` in the URL defaults to active (not a standby/inactive row).
  // Waits for the bespoke catalog fetch so we do not seed a blank draft while
  // templates are still loading.
  useEffect(() => {
    if (!selectedVersion || !selectedRogerDocFormType || !selectedSectionName) {
      setEditorTarget(null);
      return;
    }
    if (!templatesReady) {
      return;
    }

    const key = sectionEditorDraftKey(
      selectedRogerDocFormType,
      selectedVersion.name,
      selectedSectionName
    );
    const active = enabledTemplateBySection.get(selectedSectionName) ?? null;
    const existingDraft = useStore.getState().editorDraftByKey[key];

    if (
      existingDraft &&
      shouldAdoptActiveTemplateDraft(existingDraft, active)
    ) {
      const seed = buildSectionEditorSeed(selectedSectionName, active);
      if (seed) {
        setSectionEditorDraft(key, seed);
        setEditorTarget({ editingTemplate: active, draft: null });
      }
      updateUrlSelection({ layoutTemplate: null });
      return;
    }

    const layoutTemplateId = searchParams.get(
      FORM_SCHEMA_QUERY_PARAMS.layoutTemplate
    );
    if (layoutTemplateId) {
      const urlTemplate =
        visitTypeTemplates.find(
          t =>
            t.id === layoutTemplateId && t.section_name === selectedSectionName
        ) ?? null;
      if (urlTemplate) {
        if (active?.id === urlTemplate.id) {
          updateUrlSelection({ layoutTemplate: null });
        } else {
          setEditorTarget({ editingTemplate: urlTemplate, draft: null });
          const seed = buildSectionEditorSeed(selectedSectionName, urlTemplate);
          if (seed) {
            if (
              !existingDraft ||
              existingDraft.editingTemplateId !== urlTemplate.id
            ) {
              if (existingDraft) {
                setSectionEditorDraft(key, seed);
              } else {
                hydrateSectionEditorDraft({ key, seed });
              }
            }
          }
          return;
        }
      } else {
        updateUrlSelection({ layoutTemplate: null });
      }
    }

    if (
      !layoutTemplateId &&
      existingDraft &&
      isPersistedInactiveLayoutDraft(existingDraft, active)
    ) {
      const seed = buildSectionEditorSeed(selectedSectionName, active);
      if (seed) {
        setSectionEditorDraft(key, seed);
        if (active) {
          setEditorTarget({ editingTemplate: active, draft: null });
        } else {
          setEditorTarget({
            editingTemplate: null,
            draft: {
              schemaVersionName: selectedVersion.name,
              schemaVersionValue: selectedVersion.version,
              rogerDocFormType: selectedRogerDocFormType,
              sectionName: selectedSectionName,
              name: seed.name,
              description: seed.description,
              layoutJsonText: seed.layoutJsonText,
              layoutStatus: seed.status,
            },
          });
        }
      }
      return;
    }

    if (existingDraft) {
      setEditorTarget(editorTargetFromPersistedDraft(existingDraft, templates));
      return;
    }

    if (active) {
      setEditorTarget({ editingTemplate: active, draft: null });
    } else {
      setEditorTarget({
        editingTemplate: null,
        draft: {
          schemaVersionName: selectedVersion.name,
          schemaVersionValue: selectedVersion.version,
          rogerDocFormType: selectedRogerDocFormType,
          sectionName: selectedSectionName,
          name: defaultBlankSectionLayoutName(
            formatSectionDropdownLabel(
              selectedSectionName,
              formTreeLabelByCategory
            )
          ),
          description: '',
          layoutJsonText: '[]',
          layoutStatus: 'live',
        },
      });
    }
    const seed = buildSectionEditorSeed(selectedSectionName, active);
    if (seed) hydrateSectionEditorDraft({ key, seed });
  }, [
    selectedSectionName,
    selectedVersion?.name,
    selectedVersion?.version,
    selectedRogerDocFormType,
    selectedSectionDefaultTemplateId,
    templatesReady,
    enabledTemplateBySection,
    visitTypeTemplates,
    templates,
    searchParams,
    buildSectionEditorSeed,
    hydrateSectionEditorDraft,
    setSectionEditorDraft,
    updateUrlSelection,
    formTreeLabelByCategory,
  ]);

  // -- live-refresh polling (EMR-277) --
  // Forms Manager MCP can push a standby layout draft from outside this tab.
  // Poll the lightweight revisions endpoint every few seconds and, on a real
  // change, refresh the catalog and (if the open editor isn't mid-edit) the
  // currently-open draft — so a pushed change shows up without a manual
  // reload. `null` means "no poll response seen yet"; distinct from any real
  // fingerprint (including the empty-list fingerprint `''`).
  const revisionsFingerprintRef = useRef<string | null>(null);

  // Per-template MCP-push tracking for the section-tab dot / versions-modal
  // dot / editor-header pill (see the UI-indicator helpers in
  // formSchemaUtils.ts). `remotePushSeenRevisionRef` is the diff baseline —
  // each row's content signature (and `updated_at` for push timestamps) the
  // last time we saw it — and `ownSaveTemplateIdsRef` is the one-poll-cycle
  // suppression window for ids this tab just saved itself, so our own save
  // isn't misread as a push.
  const [remotePushByTemplateId, setRemotePushByTemplateId] =
    useState<RemotePushByTemplateId>({});
  const remotePushSeenRevisionRef = useRef<
    Record<string, RemotePushRevisionSnapshot>
  >({});
  const remotePushHasBaselineRef = useRef(false);
  const ownSaveTemplateIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Reset so a form-type/version switch always applies the first poll for
    // the newly-selected pin instead of comparing against the prior pin's
    // fingerprint (which could coincidentally match and skip a real change).
    revisionsFingerprintRef.current = null;
    remotePushSeenRevisionRef.current = {};
    remotePushHasBaselineRef.current = false;
    setRemotePushByTemplateId({});
  }, [
    selectedRogerDocFormType,
    selectedVersion?.name,
    selectedVersion?.version,
  ]);

  // Acknowledge section/modal dots when a template becomes the open editor
  // target; keep the header pill until a local save (EMR-277).
  useEffect(() => {
    const id = editorTarget?.editingTemplate?.id ?? null;
    if (!id) return;
    setRemotePushByTemplateId(prev => acknowledgeRemotePush(prev, id));
  }, [editorTarget?.editingTemplate?.id]);

  useEffect(() => {
    if (!selectedVersion || !selectedRogerDocFormType) return;

    let cancelled = false;

    const poll = async () => {
      let revisions: BespokeTemplateRevision[];
      try {
        const res =
          await listBespokeTemplateRevisionsBespokeTemplatesRevisionsGet({
            query: {
              form_type: selectedRogerDocFormType,
              schema_version_name: selectedVersion.name,
              form_schema_version: selectedVersion.version,
            },
            throwOnError: true,
          });
        revisions = (res?.data as BespokeTemplateRevision[] | undefined) ?? [];
      } catch (err) {
        // Silent by design: this is a background poll, not a user action —
        // a transient failure here shouldn't interrupt editing with a toast.
        console.error('Failed to poll bespoke template revisions:', err);
        return;
      }
      if (cancelled) return;

      // Remote-push detection runs on every tick, independent of the
      // fingerprint gate below — it's a cheap in-memory diff, and gating it
      // on the same fingerprint would miss a push that lands between two
      // otherwise-identical polls (e.g. a push immediately reverted).
      const { nextById, pushed } = diffRevisionsForRemotePush({
        revisions,
        previousById: remotePushSeenRevisionRef.current,
        ownSaveTemplateIds: ownSaveTemplateIdsRef.current,
        hasBaseline: remotePushHasBaselineRef.current,
      });
      remotePushSeenRevisionRef.current = nextById;
      remotePushHasBaselineRef.current = true;
      ownSaveTemplateIdsRef.current.clear();
      setRemotePushByTemplateId(prev =>
        applyRemotePushDelta(prev, {
          pushed,
          presentTemplateIds: new Set(revisions.map(r => r.id)),
        })
      );

      const fingerprint = bespokeTemplateRevisionsFingerprint(revisions);
      if (fingerprint === revisionsFingerprintRef.current) return;

      let list: BespokeTemplateResponse[];
      try {
        const res = await listBespokeTemplatesBespokeTemplatesGet({
          query: {
            form_type: selectedRogerDocFormType,
            schema_version_name: selectedVersion.name,
            form_schema_version: selectedVersion.version,
          },
          throwOnError: true,
        });
        list = (res?.data as BespokeTemplateResponse[] | undefined) ?? [];
        setTemplates(prev => upsertBespokeTemplatesLocal(prev, list));
        setTemplatesLoadFailed(false);
      } catch (err) {
        // Same silent policy as revisions poll: do not toast or wipe the catalog.
        console.error(
          'Failed to refresh bespoke templates after revisions change:',
          err
        );
        return;
      }
      if (cancelled) return;

      const key = selectedSectionName
        ? sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            selectedSectionName
          )
        : null;
      const draft = key ? useStore.getState().editorDraftByKey[key] : undefined;

      const refreshPlan = planRevisionsPollFingerprintRefresh({
        revisions,
        previousFingerprint: revisionsFingerprintRef.current,
        editingTemplateId: draft?.editingTemplateId,
        isDraftDirty: draft ? isSectionEditorDraftDirty(draft) : false,
        refreshedTemplates: list,
      });

      if (refreshPlan.kind === 'fingerprint_unchanged') return;
      if (refreshPlan.kind === 'defer_dirty_draft') return;

      if (refreshPlan.templateToSync && key) {
        const seed = buildSectionEditorSeed(
          refreshPlan.templateToSync.section_name,
          refreshPlan.templateToSync
        );
        if (seed) {
          setSectionEditorDraft(key, seed);
          setEditorTarget({
            editingTemplate: refreshPlan.templateToSync,
            draft: null,
          });
        }
      }

      revisionsFingerprintRef.current = refreshPlan.nextFingerprint;
    };

    const runPollIfVisible = () => {
      if (document.hidden) return;
      void poll();
    };

    const intervalId = setInterval(runPollIfVisible, 3000);
    runPollIfVisible();

    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        void poll();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    selectedRogerDocFormType,
    selectedVersion,
    selectedSectionName,
    buildSectionEditorSeed,
    setSectionEditorDraft,
  ]);

  const openEditorForTemplate = useCallback(
    (template: BespokeTemplateResponse) => {
      if (!selectedVersion || !selectedRogerDocFormType) return;
      setSelectedSectionName(template.section_name);
      const active =
        enabledTemplateBySection.get(template.section_name) ?? null;
      updateUrlSelection({
        section: template.section_name,
        layoutTemplate: layoutTemplateQueryValue(template, active),
      });
      setEditorTarget({ editingTemplate: template, draft: null });
      const seed = buildSectionEditorSeed(template.section_name, template);
      if (seed) {
        setSectionEditorDraft(
          sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            template.section_name
          ),
          seed
        );
      }
    },
    [
      selectedVersion,
      selectedRogerDocFormType,
      enabledTemplateBySection,
      updateUrlSelection,
      buildSectionEditorSeed,
      setSectionEditorDraft,
    ]
  );

  const templatesBySection = useMemo(() => {
    const map = new Map<string, BespokeTemplateResponse[]>();
    for (const template of visitTypeTemplates) {
      const existing = map.get(template.section_name) ?? [];
      existing.push(template);
      map.set(template.section_name, existing);
    }
    for (const rows of map.values()) {
      rows.sort((a, b) => {
        const d = bespokeStatusTier(a.status) - bespokeStatusTier(b.status);
        if (d !== 0) return d;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
    }
    return map;
  }, [visitTypeTemplates]);

  const handleCopyTemplateLayoutJson = useCallback(
    async (template: BespokeTemplateResponse | null) => {
      if (!template?.layout_json) {
        toast.error('Nothing to copy', 'This template has no assigned layout.');
        return;
      }
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(template.layout_json, null, 2)
        );
        toast.success('Copied', 'Roger Layout Schema copied to clipboard.');
      } catch {
        toast.error('Clipboard failed', 'Could not write to clipboard.');
      }
    },
    [toast]
  );

  const handleCopySectionLayoutJson = useCallback(
    async (layoutState: SectionLayoutState) => {
      await handleCopyTemplateLayoutJson(
        layoutState.kind === 'missing' ? null : layoutState.template
      );
    },
    [handleCopyTemplateLayoutJson]
  );

  const handleAssignSectionLayoutJsonFromClipboard = useCallback(
    async (sectionName: string, questions: FormTreeQuestion[]) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }

      let clipboardText = '';
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch {
        toast.error('Clipboard failed', 'Could not read from clipboard.');
        return;
      }

      const validation = validateLayoutJsonForSection(clipboardText, questions);
      if (!validation.ok) {
        toast.error('Invalid Roger Layout Schema', validation.error);
        return;
      }

      const key = sectionEditorDraftKey(
        selectedRogerDocFormType,
        selectedVersion.name,
        sectionName
      );
      const layoutJsonText = JSON.stringify(validation.layout, null, 2);
      const displayName = formatSectionDropdownLabel(
        sectionName,
        formTreeLabelByCategory
      );
      // Prefer Live/Paused primary; otherwise update any existing template for
      // the section (Standby/Draft) so paste does not create a duplicate Live.
      const templateToEdit =
        enabledTemplateBySection.get(sectionName) ??
        existingTemplateBySection.get(sectionName) ??
        null;

      handleSectionClick(sectionName);

      const existingDraft = useStore.getState().editorDraftByKey[key];
      if (!existingDraft) {
        const seed = buildSectionEditorSeed(sectionName, templateToEdit);
        if (!seed) return;
        if (templateToEdit) {
          setEditorTarget({ editingTemplate: templateToEdit, draft: null });
        } else {
          setEditorTarget({
            editingTemplate: null,
            draft: {
              schemaVersionName: selectedVersion.name,
              schemaVersionValue: selectedVersion.version,
              rogerDocFormType: selectedRogerDocFormType,
              sectionName,
              name: seed.name,
              description: seed.description,
              layoutJsonText: seed.layoutJsonText,
              layoutStatus: seed.status,
            },
          });
        }
        hydrateSectionEditorDraft({ key, seed });
      } else if (
        templateToEdit &&
        existingDraft.editingTemplateId == null &&
        !isSectionEditorDraftDirty(existingDraft)
      ) {
        const seed = buildSectionEditorSeed(sectionName, templateToEdit);
        if (seed) {
          setSectionEditorDraft(key, seed);
          setEditorTarget({ editingTemplate: templateToEdit, draft: null });
        }
      }

      setSectionEditorField(key, { layoutJsonText });
      toast.success(
        'Layout pasted',
        `${displayName} updated locally. Save when ready.`
      );
    },
    [
      buildSectionEditorSeed,
      enabledTemplateBySection,
      existingTemplateBySection,
      formTreeLabelByCategory,
      handleSectionClick,
      hydrateSectionEditorDraft,
      selectedRogerDocFormType,
      selectedVersion,
      setSectionEditorDraft,
      setSectionEditorField,
      toast,
    ]
  );

  const handlePasteTemplateLayoutFromClipboard = useCallback(
    async (
      template: BespokeTemplateResponse,
      questions: FormTreeQuestion[]
    ) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }

      let clipboardText = '';
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch {
        toast.error('Clipboard failed', 'Could not read from clipboard.');
        return;
      }

      const validation = validateLayoutJsonForSection(clipboardText, questions);
      if (!validation.ok) {
        toast.error('Invalid Roger Layout Schema', validation.error);
        return;
      }

      const sectionName = template.section_name;
      const key = sectionEditorDraftKey(
        selectedRogerDocFormType,
        selectedVersion.name,
        sectionName
      );
      const layoutJsonText = JSON.stringify(validation.layout, null, 2);
      const displayName = formatSectionDropdownLabel(
        sectionName,
        formTreeLabelByCategory
      );

      openEditorForTemplate(template);

      setSectionEditorField(key, { layoutJsonText });
      toast.success(
        'Layout pasted',
        `${displayName} (${template.name}) updated locally. Save when ready.`
      );
    },
    [
      formTreeLabelByCategory,
      openEditorForTemplate,
      selectedRogerDocFormType,
      selectedVersion,
      setSectionEditorField,
      toast,
    ]
  );

  const validationSeedVersionsByFormType = useMemo(() => {
    if (!selectedRogerDocFormType || versions.length === 0) return undefined;
    return new Map([[selectedRogerDocFormType, versions]]);
  }, [selectedRogerDocFormType, versions]);

  const handleGenerateSectionLayout = useCallback(
    (
      sectionName: string,
      questions: FormTreeQuestion[],
      prompt = DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT
    ) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }
      if (!selectedVersion.version) {
        toast.error(
          'Missing schema version',
          'The selected schema is missing a version identifier.'
        );
        return;
      }
      const trimmed = prompt.trim();
      if (!trimmed) {
        toast.error('Prompt required', 'Enter a prompt to generate a layout.');
        return;
      }
      startFormSchemaLayoutGeneration({
        formType: selectedRogerDocFormType,
        schemaVersionName: selectedVersion.name,
        formSchemaVersion: selectedVersion.version,
        sectionName,
        questions,
        prompt: trimmed,
        visitTypeSummary:
          selectedCatalogEntry?.display_label ?? selectedRogerDocFormType,
        canonicalVisitTypeId: canonicalVisitForApi,
        isOasis: selectedCatalogEntry?.is_oasis,
        usedDefaultPrompt: trimmed === DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT,
        onTemplateSaved: upsertTemplateLocal,
        toast,
      });
    },
    [
      canonicalVisitForApi,
      selectedCatalogEntry,
      selectedRogerDocFormType,
      selectedVersion,
      startFormSchemaLayoutGeneration,
      toast,
      upsertTemplateLocal,
    ]
  );

  const handleCreateSectionLayout = useCallback(
    (sectionName: string) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }
      setSelectedSectionName(sectionName);
      updateUrlSelection({ section: sectionName, layoutTemplate: null });
      setEditorTarget({
        editingTemplate: null,
        draft: {
          schemaVersionName: selectedVersion.name,
          schemaVersionValue: selectedVersion.version,
          rogerDocFormType: selectedRogerDocFormType,
          sectionName,
          name: defaultBlankSectionLayoutName(
            formatSectionDropdownLabel(sectionName, formTreeLabelByCategory)
          ),
          description: '',
          layoutJsonText: '[]',
          layoutStatus: 'live',
        },
      });
      const seed = buildSectionEditorSeed(sectionName, null);
      if (seed) {
        setSectionEditorDraft(
          sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            sectionName
          ),
          seed
        );
      }
    },
    [
      buildSectionEditorSeed,
      formTreeLabelByCategory,
      selectedRogerDocFormType,
      selectedVersion,
      setSectionEditorDraft,
      toast,
      updateUrlSelection,
    ]
  );

  const selectedSectionQuestions =
    selectedSectionName && formTree ? formTree[selectedSectionName] : null;
  const selectedSectionEditorKey =
    selectedVersion && selectedSectionName && selectedRogerDocFormType
      ? sectionEditorDraftKey(
          selectedRogerDocFormType,
          selectedVersion.name,
          selectedSectionName
        )
      : null;
  const selectedSectionEditorDraft = selectedSectionEditorKey
    ? editorDraftByKey[selectedSectionEditorKey]
    : undefined;
  const editorTargetTemplateId = editorTarget?.editingTemplate?.id ?? null;
  /** Only render once section, target, and store draft all agree on the same form. */
  const editorMatchesSection =
    !!editorTarget &&
    !!selectedSectionName &&
    (editorTarget.editingTemplate?.section_name === selectedSectionName ||
      editorTarget.draft?.sectionName === selectedSectionName) &&
    selectedSectionEditorDraft != null &&
    selectedSectionEditorDraft.editingTemplateId === editorTargetTemplateId;

  const versionsSectionQuestions =
    versionsSection && formTree ? formTree[versionsSection] : null;
  const versionsSectionTemplates = versionsSection
    ? (templatesBySection.get(versionsSection) ?? [])
    : [];
  const customGenerateInProgress =
    customGenerateDialog != null &&
    selectedVersion != null &&
    sectionGenerationStatusByKey[
      layoutGenerationKey(
        selectedVersion.name,
        customGenerateDialog.sectionName
      )
    ]?.state === 'loading';

  return (
    <div className='space-y-6'>
      {/* Section 1: Schema Version Browser */}
      <div className='space-y-4'>
        <SchemaVersionBrowser
          actions={
            <FormSchemasHeader
              catalogLoading={catalogLoading}
              catalogEmpty={formTypeCatalog.length === 0}
              templates={templates}
              onImport={() => setImportModalOpen(true)}
              onExport={() => setExportModalOpen(true)}
              onOpenValidation={() => setValidationResultsOpen(true)}
              onOpenBulk={openBulkBespokeDialog}
            />
          }
          formTypeCatalog={formTypeCatalog}
          catalogLoading={catalogLoading}
          selectedRogerDocFormType={selectedRogerDocFormType}
          selectedCatalogLabel={selectedCatalogEntry?.display_label}
          onFormTypeChange={nextFormType => {
            setSelectedRogerDocFormType(nextFormType);
            setVersions([]);
            setVersionsError(null);
            setVersionsLoading(true);
            setSelectedVersion(null);
            setSelectedSectionName(null);
            setFormTree(null);
            setFormTreeSectionOrder(null);
            setTreeLoading(false);
            setEditorTarget(null);
            setVersionsSection(null);
            initialVersionUrlConsumedRef.current = true;
            initialUrlStateRef.current = {
              ...initialUrlStateRef.current,
              formType: nextFormType,
              schemaName: null,
              schemaVersion: null,
              section: null,
              layoutTemplate: null,
            };
            updateUrlSelection({
              formType: nextFormType,
              schemaName: null,
              schemaVersion: null,
              section: null,
              layoutTemplate: null,
            });
          }}
          versions={versions}
          versionsLoading={versionsLoading}
          versionsError={versionsError}
          selectedVersion={selectedVersion}
          onVersionSelect={handleVersionClick}
          templates={templates}
          pinsForCurrentFormType={pinsForCurrentFormType}
          onOpenBulk={openBulkBespokeDialog}
        />

        {selectedVersion &&
          selectedVersion.form_type === selectedRogerDocFormType && (
            <FormSchemaStudio
              selectedRogerDocFormType={selectedRogerDocFormType}
              selectedVersion={selectedVersion}
              selectedSectionName={selectedSectionName}
              formTree={formTree}
              formTreeLabelByCategory={formTreeLabelByCategory}
              orderedFormTreeSections={orderedFormTreeSections}
              treeLoading={treeLoading}
              formTypeCatalog={formTypeCatalog}
              catalogLoading={catalogLoading}
              editorTarget={editorTarget}
              editorMatchesSection={editorMatchesSection}
              selectedSectionQuestions={selectedSectionQuestions}
              enabledTemplateBySection={enabledTemplateBySection}
              templatesBySection={templatesBySection}
              editorDraftByKey={editorDraftByKey}
              sectionGenerationStatusByKey={sectionGenerationStatusByKey}
              remotePushByTemplateId={remotePushByTemplateId}
              saving={saving}
              getSectionConsistency={getSectionConsistency}
              onSelectSection={handleSectionClick}
              onSaveJson={handlePersistVibecoderJson}
              onGenerate={(section, questions) =>
                void handleGenerateSectionLayout(section, questions)
              }
              onGenerateCustom={setCustomGenerateDialog}
              onAddFromClipboard={(section, questions) =>
                void handleAssignSectionLayoutJsonFromClipboard(
                  section,
                  questions
                )
              }
              onCopyLayout={layoutState =>
                void handleCopySectionLayoutJson(layoutState)
              }
              onViewQuestions={(section, questions) =>
                setQuestionDialog({ sectionName: section, questions })
              }
              onOpenVersions={section => setVersionsSection(section)}
              previewViewport={previewViewport}
              onPreviewViewportChange={setPreviewViewport}
            />
          )}
      </div>

      <BulkTemplateStatusDialog
        dialog={bulkBespokeDialog}
        busy={bulkBespokeBusy}
        onClose={closeBulkBespokeDialog}
        onConfirm={() => void runBulkBespokeConfirmed()}
      />

      <CustomGenerateDialog
        target={customGenerateDialog}
        inProgress={customGenerateInProgress}
        onClose={() => setCustomGenerateDialog(null)}
        onSubmit={prompt => {
          const target = customGenerateDialog;
          if (!target) return;
          void handleGenerateSectionLayout(
            target.sectionName,
            target.questions,
            prompt
          );
        }}
      />

      <BespokeTemplatesImportDialog
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        formTypeCatalog={formTypeCatalog}
        catalogLoading={catalogLoading}
        onImported={() => {
          fetchTemplates().catch(err =>
            console.error('Failed to refresh templates after import:', err)
          );
        }}
      />

      <BespokeTemplatesExportDialog
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        formTypeCatalog={formTypeCatalog}
        catalogLoading={catalogLoading}
      />

      <QuestionsDialog
        sectionName={questionDialog?.sectionName ?? null}
        questions={questionDialog?.questions ?? []}
        onClose={() => setQuestionDialog(null)}
      />

      {versionsSection && versionsSectionQuestions && (
        <SectionVersionsDialog
          open={versionsSection != null}
          onOpenChange={open => {
            if (!open) setVersionsSection(null);
          }}
          sectionDisplayName={formatSectionDropdownLabel(
            versionsSection,
            formTreeLabelByCategory
          )}
          templates={versionsSectionTemplates}
          questions={versionsSectionQuestions}
          busyTemplateId={templateStatusBusyId}
          getRemotePush={templateId =>
            unacknowledgedRemotePush(remotePushByTemplateId, templateId)
          }
          generationStatus={
            selectedVersion
              ? sectionGenerationStatusByKey[
                  layoutGenerationKey(selectedVersion.name, versionsSection)
                ]
              : undefined
          }
          onCreateLayout={() => {
            handleCreateSectionLayout(versionsSection);
            setVersionsSection(null);
          }}
          onGenerateLayout={() =>
            void handleGenerateSectionLayout(
              versionsSection,
              versionsSectionQuestions
            )
          }
          onGenerateLayoutWithCustomPrompt={() => {
            setCustomGenerateDialog({
              sectionName: versionsSection,
              displayName: formatSectionDropdownLabel(
                versionsSection,
                formTreeLabelByCategory
              ),
              questions: versionsSectionQuestions,
            });
            setVersionsSection(null);
          }}
          onAddLayoutFromClipboard={() =>
            void handleAssignSectionLayoutJsonFromClipboard(
              versionsSection,
              versionsSectionQuestions
            )
          }
          onEditTemplate={template => openEditorForTemplate(template)}
          onCopyTemplate={template =>
            void handleCopyTemplateLayoutJson(template)
          }
          onPasteTemplate={template =>
            void handlePasteTemplateLayoutFromClipboard(
              template,
              versionsSectionQuestions
            )
          }
          onSetTemplateStatus={(template, status) =>
            void handleSetBespokeTemplateStatus(template, status)
          }
          onDeleteTemplate={template => void handleDelete(template.id)}
          onEditTemplateDetails={handleSaveTemplateDetails}
        />
      )}

      <ValidationResultsDialog
        open={validationResultsOpen}
        onOpenChange={setValidationResultsOpen}
        templates={templates}
        formTypeCatalog={formTypeCatalog}
        seedVersionsByFormType={validationSeedVersionsByFormType}
      />
    </div>
  );
};

/**
 * Permission gate for the Forms Manager workspace.
 *
 * Forms Manager is Roger-employee tooling and its API is super-user only
 * (EMR-1541); gating on `emr:manage` would render the page for an agency admin
 * whose every request the backend then refuses. The check lives out here rather
 * than inside the workspace so a denied visitor never mounts it: the workspace
 * fetches the catalog and templates on mount and polls `/revisions` every three
 * seconds, and a guard that returns after those hooks are declared still runs
 * them, turning an Access Denied card into a standing stream of 403s.
 */
const FormSchemas: React.FC = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );

  if (!hasPermission(PermissionSlug.ADMIN_SUPER_USER)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <ShieldX className='h-5 w-5' />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-muted-foreground'>
            You don&apos;t have permission to access Form Schema management.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <FormSchemasWorkspace />;
};

export default FormSchemas;
